import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import { FiX, FiBookOpen, FiTrash2, FiPlus } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { createTeamSop, deleteTeamSop, getTeamSops, updateTeamSop } from '../api/sopApi';
import { SOP_AUDIENCE_OPTIONS } from '../constants/sopAudiences';

const fieldStyle = {
  width: '100%',
  padding: '0.45rem 0.55rem',
  borderRadius: 6,
  border: '1px solid #e2e8f0',
  fontSize: '0.85rem',
  boxSizing: 'border-box',
};

const emptyDraft = () => ({
  title: '',
  document_url: '',
  audience_role: 'developer',
});

export default function TeamSopManageDialog({ open, onClose }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState(emptyDraft);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['team-sops'],
    queryFn: getTeamSops,
    enabled: open,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['team-sops'] });

  const createMutation = useMutation({
    mutationFn: () =>
      createTeamSop({
        title: draft.title.trim(),
        document_url: draft.document_url.trim(),
        audience_role: draft.audience_role,
      }),
    onSuccess: () => {
      toast.success('Document added');
      setDraft(emptyDraft());
      invalidate();
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to add document'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }) => updateTeamSop(id, body),
    onSuccess: () => {
      toast.success('Document updated');
      invalidate();
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to update'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteTeamSop(id),
    onSuccess: () => {
      toast.success('Document removed');
      invalidate();
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to remove'),
  });

  const items = data?.items || [];
  const busy = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  const handleAdd = () => {
    if (!draft.title.trim() || !draft.document_url.trim()) {
      toast.error('Title and document link are required');
      return;
    }
    createMutation.mutate();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800 }}>
          <FiBookOpen size={20} color="#1272B3" />
          Manage Documents
        </span>
        <IconButton onClick={onClose} aria-label="close" size="small">
          <FiX />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: '#64748b', lineHeight: 1.5 }}>
          Add shared document links (Google Drive, Confluence, etc.) by audience — e.g. developer handbook, QA checklist.
          Everyone can open them from <strong>My Tasks → Documents</strong>.
        </p>

        <div
          style={{
            padding: '1rem',
            background: '#f8fafc',
            borderRadius: 10,
            border: '1px solid #e2e8f0',
            marginBottom: '1.25rem',
          }}
        >
          <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.9rem', fontWeight: 800, color: '#1e293b' }}>
            Add new Document
          </h4>
          <div style={{ display: 'grid', gap: 10 }}>
            <label style={{ display: 'block' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b' }}>Title</span>
              <input
                type="text"
                value={draft.title}
                onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                placeholder="e.g. Developer onboarding guide"
                style={{ ...fieldStyle, marginTop: 4 }}
              />
            </label>
            <label style={{ display: 'block' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b' }}>Document link</span>
              <input
                type="url"
                value={draft.document_url}
                onChange={(e) => setDraft((d) => ({ ...d, document_url: e.target.value }))}
                placeholder="https://..."
                style={{ ...fieldStyle, marginTop: 4 }}
              />
            </label>
            <label style={{ display: 'block' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b' }}>Audience</span>
              <select
                value={draft.audience_role}
                onChange={(e) => setDraft((d) => ({ ...d, audience_role: e.target.value }))}
                style={{ ...fieldStyle, marginTop: 4 }}
              >
                {SOP_AUDIENCE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              onClick={handleAdd}
              disabled={busy}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                alignSelf: 'flex-start',
                padding: '0.5rem 1rem',
                borderRadius: 8,
                border: 'none',
                background: '#1272B3',
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.85rem',
                cursor: busy ? 'not-allowed' : 'pointer',
              }}
            >
              <FiPlus size={14} />
              {createMutation.isPending ? 'Adding…' : 'Add Document'}
            </button>
          </div>
        </div>

        <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.9rem', fontWeight: 800, color: '#1e293b' }}>
          Published documents ({items.length})
        </h4>
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '1.5rem' }}>
            <CircularProgress size={28} />
          </div>
        ) : isError ? (
          <p style={{ color: '#b91c1c', fontSize: '0.85rem' }}>
            {error?.response?.data?.message || 'Failed to load documents'}
          </p>
        ) : items.length === 0 ? (
          <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No documents yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {items.map((doc) => (
              <SopEditRow
                key={doc.id}
                doc={doc}
                busy={busy}
                onSave={(body) => updateMutation.mutate({ id: doc.id, body })}
                onDelete={() => {
                  if (!window.confirm(`Remove "${doc.title}"?`)) return;
                  deleteMutation.mutate(doc.id);
                }}
              />
            ))}
          </div>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <button
          type="button"
          onClick={onClose}
          style={{
            padding: '0.5rem 1rem',
            borderRadius: 8,
            border: '1px solid #e2e8f0',
            background: '#fff',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Done
        </button>
      </DialogActions>
    </Dialog>
  );
}

function SopEditRow({ doc, busy, onSave, onDelete }) {
  const [title, setTitle] = useState(doc.title);
  const [url, setUrl] = useState(doc.document_url);
  const [audience, setAudience] = useState(doc.audience_role || 'all');

  useEffect(() => {
    setTitle(doc.title);
    setUrl(doc.document_url);
    setAudience(doc.audience_role || 'all');
  }, [doc.id, doc.title, doc.document_url, doc.audience_role]);

  const dirty =
    title.trim() !== doc.title ||
    url.trim() !== doc.document_url ||
    audience !== (doc.audience_role || 'all');

  return (
    <div
      style={{
        padding: '0.75rem',
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        background: '#fff',
      }}
    >
      <div style={{ display: 'grid', gap: 8 }}>
        <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} style={fieldStyle} />
        <input type="url" value={url} onChange={(e) => setUrl(e.target.value)} style={fieldStyle} />
        <select value={audience} onChange={(e) => setAudience(e.target.value)} style={fieldStyle}>
          {SOP_AUDIENCE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          disabled={!dirty || busy}
          onClick={() =>
            onSave({
              title: title.trim(),
              document_url: url.trim(),
              audience_role: audience,
            })
          }
          style={{
            padding: '0.35rem 0.75rem',
            borderRadius: 6,
            border: 'none',
            background: dirty ? '#1272B3' : '#cbd5e1',
            color: '#fff',
            fontWeight: 700,
            fontSize: '0.78rem',
            cursor: !dirty || busy ? 'not-allowed' : 'pointer',
          }}
        >
          Save
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onDelete}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '0.35rem 0.75rem',
            borderRadius: 6,
            border: '1px solid #fecaca',
            background: '#fef2f2',
            color: '#b91c1c',
            fontWeight: 700,
            fontSize: '0.78rem',
            cursor: busy ? 'not-allowed' : 'pointer',
          }}
        >
          <FiTrash2 size={12} />
          Remove
        </button>
      </div>
    </div>
  );
}
